import { useEffect } from 'react';
import { usePrevious } from 'react-use';

import { useDoesEntityHaveActiveSync } from '@/modules/workflowTrackers/hooks/useDoesEntityHaveActiveSync';
import { diagnostics } from '@/utils/diagnostics';

import { useEstatePageEntityLazyQuery } from '../graphql/EstatePageData.generated';

export function useEntitySyncingState(entityId: string) {
  const [getEntity, { loading: reloadingEntityData }] =
    useEstatePageEntityLazyQuery();
  const doesEntityHaveActiveSync = useDoesEntityHaveActiveSync(entityId);
  const previousSyncState = usePrevious(doesEntityHaveActiveSync);
  const previousEntityId = usePrevious(entityId);

  useEffect(() => {
    // Only act when current value is a known boolean
    if (typeof doesEntityHaveActiveSync !== 'boolean') {
      return;
    }

    // Skip if entity changed (reset state)
    if (previousEntityId !== entityId) {
      return;
    }

    // Detect transition true -> false (sync completed)
    if (previousSyncState === true && doesEntityHaveActiveSync === false) {
      diagnostics.debug('Refetching entity data after sync', { entityId });
      void getEntity({
        variables: { entityId },
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first',
      });
    }
  }, [
    doesEntityHaveActiveSync,
    entityId,
    getEntity,
    previousEntityId,
    previousSyncState,
  ]);

  return doesEntityHaveActiveSync === true || reloadingEntityData;
}
